import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../components/ToastContainer';
import * as api from '../../services/api';
import '../../styles/admin-ui.css';
import './Companies.css';

const PLAN_LABELS = {
  free: 'Free',
  basic: 'Basic',
  pro: 'Professional',
  enterprise: 'Enterprise',
};

const planChipClass = (planType) => {
  switch (planType) {
    case 'basic':
      return 'aui-chip signal';
    case 'pro':
      return 'aui-chip ok';
    case 'enterprise':
      return 'aui-chip warn';
    default:
      return 'aui-chip';
  }
};

const Companies = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getAdminCompanies();
      setCompanies(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error('Error fetching companies:', err);
      const message = err.message || 'Failed to load companies';
      setError(message);
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (companyId) => {
    try {
      const details = await api.getAdminCompanyInfo(companyId);
      setCompanyDetails(details);
      setShowDetailsModal(true);
    } catch (err) {
      showError(err.message || t('systemAdmin.companies.loadDetailsFailed', 'Failed to load company details'));
    }
  };

  const handleEditCompany = (company) => {
    setSelectedCompany(company);
    setShowEditModal(true);
  };

  const handleManageLicense = (company) => {
    setSelectedCompany(company);
    setShowLicenseModal(true);
  };

  const handleDeleteCompany = async (companyId, companyName) => {
    if (!window.confirm(`Are you sure you want to delete ${companyName}? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.deleteAdminCompany(companyId);
      showSuccess(t('systemAdmin.companies.deleteSuccess', 'Company deleted successfully'));
      if (showDetailsModal && companyDetails?.company?.id === companyId) {
        setShowDetailsModal(false);
        setCompanyDetails(null);
      }
      fetchCompanies();
    } catch (err) {
      showError(err.message || t('systemAdmin.companies.deleteFailed', 'Failed to delete company'));
    }
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = {
      name: formData.get('name'),
      domain: formData.get('domain'),
    };

    try {
      await api.updateAdminCompany(selectedCompany.id, data);
      showSuccess(t('systemAdmin.companies.updateSuccess', 'Company updated successfully'));
      setShowEditModal(false);
      fetchCompanies();
    } catch (err) {
      showError(err.message || t('systemAdmin.companies.updateFailed', 'Failed to update company'));
    }
  };

  const handleUpdateLicense = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = {
      plan_type: formData.get('plan_type'),
      max_connections: parseInt(formData.get('max_connections'), 10),
      max_users: parseInt(formData.get('max_users'), 10),
      expires_at: formData.get('expires_at') || null,
      is_active: formData.get('is_active') === 'true',
    };

    try {
      await api.updateAdminCompanyLicense(selectedCompany.id, data);
      showSuccess(t('systemAdmin.companies.licenseUpdateSuccess', 'License updated successfully'));
      setShowLicenseModal(false);
      fetchCompanies();
    } catch (err) {
      showError(err.message || t('systemAdmin.companies.licenseUpdateFailed', 'Failed to update license'));
    }
  };

  const getPlanLabel = (planType) => PLAN_LABELS[planType] || planType || 'Free';

  const stats = useMemo(
    () => ({
      totalCompanies: companies.length,
      totalUsers: companies.reduce((sum, c) => sum + (c.user_count || 0), 0),
      activeCompanies: companies.filter((c) => c.is_active).length,
      totalLocations: companies.reduce((sum, c) => sum + (c.license_count || 0), 0),
    }),
    [companies]
  );

  const filteredCompanies = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter((company) => {
      const name = (company.name || '').toLowerCase();
      const domain = (company.domain || '').toLowerCase();
      const id = String(company.id || '');
      return name.includes(q) || domain.includes(q) || id.includes(q);
    });
  }, [companies, searchQuery]);

  if (loading) {
    return (
      <div className="aui-page companies-page">
        <button type="button" className="aui-back" onClick={() => navigate('/dashboard/admin')}>
          ← Back
        </button>
        <div className="aui-empty">
          <div className="aui-spinner" />
          <p>{t('common.loading', 'Loading…')}</p>
        </div>
      </div>
    );
  }

  if (error && companies.length === 0) {
    return (
      <div className="aui-page companies-page">
        <button type="button" className="aui-back" onClick={() => navigate('/dashboard/admin')}>
          ← Back
        </button>
        <div className="aui-empty">
          <h3>Unable to load companies</h3>
          <p>{error}</p>
          <button type="button" className="aui-btn primary" onClick={fetchCompanies}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="aui-page companies-page">
      <button type="button" className="aui-back" onClick={() => navigate('/dashboard/admin')}>
        ← Back
      </button>

      <div className="aui-hero">
        <div>
          <p className="aui-kicker">System admin</p>
          <h1>Companies</h1>
          <p className="aui-lead">View and manage organizations, seats, and licenses.</p>
        </div>
        <div className="aui-hero-actions">
          <button type="button" className="aui-btn ghost" onClick={fetchCompanies}>
            Refresh
          </button>
        </div>
      </div>

      <div className="aui-stats">
        <article className="aui-stat">
          <span>Total companies</span>
          <strong>{stats.totalCompanies}</strong>
        </article>
        <article className="aui-stat">
          <span>Total users</span>
          <strong>{stats.totalUsers}</strong>
        </article>
        <article className="aui-stat">
          <span>Active locations</span>
          <strong>{stats.totalLocations}</strong>
        </article>
        <article className="aui-stat">
          <span>Active companies</span>
          <strong>{stats.activeCompanies}</strong>
        </article>
      </div>

      <div className="aui-toolbar">
        <input
          type="search"
          className="aui-search"
          placeholder="Search by name, domain, or ID"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <span className="aui-chip">{filteredCompanies.length} shown</span>
      </div>

      <div className="aui-panel">
        {filteredCompanies.length === 0 ? (
          <div className="aui-empty">
            <h3>No companies found</h3>
            <p>
              {searchQuery
                ? 'Try a different search query.'
                : 'Companies will appear here once they are registered.'}
            </p>
          </div>
        ) : (
          <table className="aui-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>License</th>
                <th>Users</th>
                <th>Locations</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.map((company) => (
                <tr key={company.id}>
                  <td>
                    <div className="companies-name">
                      <strong>{company.name}</strong>
                      <span>
                        {company.domain || `ID ${company.id}`}
                      </span>
                    </div>
                  </td>
                  <td>
                    <span className={planChipClass(company.plan_type)}>
                      {getPlanLabel(company.plan_type)}
                    </span>
                  </td>
                  <td>{company.user_count ?? 0}</td>
                  <td>{company.license_count ?? 0}</td>
                  <td>
                    <span className={`aui-chip ${company.is_active ? 'ok' : 'danger'}`}>
                      {company.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    {company.created_at
                      ? new Date(company.created_at).toLocaleDateString()
                      : '—'}
                  </td>
                  <td>
                    <div className="aui-row-actions">
                      <button type="button" onClick={() => handleViewDetails(company.id)}>
                        Details
                      </button>
                      <button type="button" onClick={() => handleEditCompany(company)}>
                        Edit
                      </button>
                      <button type="button" onClick={() => handleManageLicense(company)}>
                        License
                      </button>
                      <button
                        type="button"
                        className="danger"
                        onClick={() => handleDeleteCompany(company.id, company.name)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showEditModal && selectedCompany && (
        <div className="aui-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="aui-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Edit company</h2>
            <form className="aui-form" onSubmit={handleUpdateCompany}>
              <label>
                Company name
                <input
                  type="text"
                  name="name"
                  defaultValue={selectedCompany.name}
                  required
                />
              </label>
              <label>
                Domain
                <input
                  type="text"
                  name="domain"
                  defaultValue={selectedCompany.domain || ''}
                />
              </label>
              <div className="aui-form-actions">
                <button
                  type="button"
                  className="aui-btn ghost"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="aui-btn primary">
                  Save changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showLicenseModal && selectedCompany && (
        <div className="aui-modal-overlay" onClick={() => setShowLicenseModal(false)}>
          <div className="aui-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Manage license</h2>
            <p className="companies-modal-lead">{selectedCompany.name}</p>
            <form className="aui-form" onSubmit={handleUpdateLicense}>
              <label>
                Plan type
                <select name="plan_type" defaultValue={selectedCompany.plan_type || 'free'}>
                  <option value="free">Free</option>
                  <option value="basic">Basic</option>
                  <option value="pro">Professional</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </label>
              <label>
                Max connections
                <input
                  type="number"
                  name="max_connections"
                  defaultValue={selectedCompany.max_connections || 2}
                  min="1"
                  required
                />
              </label>
              <label>
                Max users
                <input
                  type="number"
                  name="max_users"
                  defaultValue={selectedCompany.max_users || 5}
                  min="1"
                  required
                />
              </label>
              <label>
                Expires at (optional)
                <input
                  type="date"
                  name="expires_at"
                  defaultValue={
                    selectedCompany.expires_at
                      ? selectedCompany.expires_at.split('T')[0]
                      : ''
                  }
                />
              </label>
              <label>
                Status
                <select
                  name="is_active"
                  defaultValue={selectedCompany.is_active ? 'true' : 'false'}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </label>
              <div className="aui-form-actions">
                <button
                  type="button"
                  className="aui-btn ghost"
                  onClick={() => setShowLicenseModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="aui-btn primary">
                  Update license
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDetailsModal && companyDetails && (
        <div className="aui-modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="aui-modal wide" onClick={(e) => e.stopPropagation()}>
            <h2>Company details</h2>
            <p className="companies-modal-lead">{companyDetails.company?.name}</p>

            <div className="companies-details">
              <div>
                <h3>Company</h3>
                <div className="companies-detail-grid">
                  <div>
                    <span>ID</span>
                    <strong>{companyDetails.company?.id}</strong>
                  </div>
                  <div>
                    <span>Name</span>
                    <strong>{companyDetails.company?.name}</strong>
                  </div>
                  <div>
                    <span>Domain</span>
                    <strong>{companyDetails.company?.domain || 'N/A'}</strong>
                  </div>
                  <div>
                    <span>Created</span>
                    <strong>
                      {companyDetails.company?.created_at
                        ? new Date(companyDetails.company.created_at).toLocaleString()
                        : '—'}
                    </strong>
                  </div>
                </div>
              </div>

              {companyDetails.license && (
                <div>
                  <h3>License</h3>
                  <div className="companies-detail-grid">
                    <div>
                      <span>Plan</span>
                      <strong>
                        <span className={planChipClass(companyDetails.license.plan_type)}>
                          {getPlanLabel(companyDetails.license.plan_type)}
                        </span>
                      </strong>
                    </div>
                    <div>
                      <span>Max connections</span>
                      <strong>{companyDetails.license.max_connections}</strong>
                    </div>
                    <div>
                      <span>Max users</span>
                      <strong>{companyDetails.license.max_users}</strong>
                    </div>
                    <div>
                      <span>Current users</span>
                      <strong>{companyDetails.user_count ?? 0}</strong>
                    </div>
                    <div>
                      <span>Status</span>
                      <strong>
                        <span
                          className={`aui-chip ${
                            companyDetails.license.is_active ? 'ok' : 'danger'
                          }`}
                        >
                          {companyDetails.license.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </strong>
                    </div>
                    {companyDetails.license.expires_at && (
                      <div>
                        <span>Expires</span>
                        <strong>
                          {new Date(companyDetails.license.expires_at).toLocaleDateString()}
                        </strong>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {companyDetails.invoices && companyDetails.invoices.length > 0 && (
                <div>
                  <h3>Recent invoices</h3>
                  <div className="companies-invoices">
                    {companyDetails.invoices.slice(0, 5).map((invoice) => (
                      <div key={invoice.id} className="companies-invoice">
                        <div>
                          <strong>
                            {Number(invoice.amount).toFixed(2)} {invoice.currency}
                          </strong>
                          <span>
                            {invoice.billing_date
                              ? new Date(invoice.billing_date).toLocaleDateString()
                              : '—'}
                          </span>
                        </div>
                        <span
                          className={`aui-chip ${
                            invoice.status === 'paid'
                              ? 'ok'
                              : invoice.status === 'overdue'
                                ? 'danger'
                                : 'warn'
                          }`}
                        >
                          {invoice.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="aui-form-actions">
              <button
                type="button"
                className="aui-btn danger"
                onClick={() =>
                  handleDeleteCompany(
                    companyDetails.company.id,
                    companyDetails.company.name
                  )
                }
              >
                Delete
              </button>
              <button
                type="button"
                className="aui-btn ghost"
                onClick={() => setShowDetailsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Companies;
